import React, { useState, useRef } from 'react';
import * as XLSX from 'xlsx';

const ExcelUpload = ({ 
  onDataLoaded, 
  maxRows = 1000, 
  title = "📤 Bulk Upload Excel",
  description = "Upload Excel file with ID and TEXT columns",
  sampleType = 'barcode' // 'barcode' or 'qr'
}) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [rowCount, setRowCount] = useState(0);
  const fileInputRef = useRef(null);

  const validateRow = (row, index) => {
    const errors = [];
    // Check if ID exists (required)
    if (!row.id || row.id.trim() === '') {
      errors.push(`Row ${index + 1}: ID is required`);
    }
    // Check if TEXT exists (required for QR, optional for barcode)
    if (sampleType === 'qr' && (!row.text || row.text.trim() === '')) {
      errors.push(`Row ${index + 1}: Text is required for QR codes`);
    }
    return errors;
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel'
    ];
    
    if (!validTypes.includes(file.type) && 
        !file.name.endsWith('.xlsx') && 
        !file.name.endsWith('.xls')) {
      setError('Please upload a valid Excel file (.xlsx or .xls)');
      return;
    }

    setFile(file);
    setError('');
    setSuccess('');
    processFile(file);
  };

  const processFile = (file) => {
    setUploading(true);
    setError('');
    setSuccess('');
    setRowCount(0);

    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = e.target.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet);

        if (jsonData.length === 0) {
          setError('The Excel file is empty. Please add data.');
          setUploading(false);
          return;
        }

        // Check if file has required columns
        const headers = Object.keys(jsonData[0]);
        let requiredColumns = ['id'];
        
        // For QR, text is required
        if (sampleType === 'qr') {
          requiredColumns = ['id', 'text'];
        }
        
        const missingColumns = requiredColumns.filter(col => 
          !headers.some(h => h.toLowerCase() === col.toLowerCase())
        );

        if (missingColumns.length > 0) {
          setError(`Missing required columns: ${missingColumns.join(', ')}. 
            Please ensure your Excel has columns: ${requiredColumns.join(', ')}`);
          setUploading(false);
          return;
        }

        // Map columns (case insensitive)
        const mappedData = jsonData.map(row => {
          const urlKey = Object.keys(row).find(k => k.toLowerCase() === 'url');
          const idKey = Object.keys(row).find(k => k.toLowerCase() === 'id');
          const textKey = Object.keys(row).find(k => k.toLowerCase() === 'text');
          
          return {
            url: urlKey ? row[urlKey]?.toString().trim() || '' : '',
            id: idKey ? row[idKey]?.toString().trim() || '' : '',
            text: textKey ? row[textKey]?.toString().trim() || '' : (sampleType === 'barcode' ? row[idKey]?.toString().trim() || '' : '')
          };
        });

        // Validate row count
        if (mappedData.length > maxRows) {
          setError(`Too many rows (${mappedData.length}). Maximum allowed is ${maxRows} rows.`);
          setUploading(false);
          return;
        }

        // Validate each row
        const validationErrors = [];
        mappedData.forEach((row, index) => {
          const errors = validateRow(row, index);
          validationErrors.push(...errors);
        });

        if (validationErrors.length > 0) {
          setError(`Validation errors found:\n${validationErrors.join('\n')}`);
          setUploading(false);
          return;
        }

        setRowCount(mappedData.length);
        setSuccess(`✅ Successfully loaded ${mappedData.length} rows from Excel!`);
        
        // Pass data to parent component
        onDataLoaded(mappedData);
        
      } catch (err) {
        console.error('Excel processing error:', err);
        setError(`Failed to process Excel file: ${err.message}`);
      } finally {
        setUploading(false);
      }
    };

    reader.onerror = () => {
      setError('Failed to read the file. Please try again.');
      setUploading(false);
    };

    reader.readAsBinaryString(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      setFile(file);
      processFile(file);
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    setError('');
    setSuccess('');
    setRowCount(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onDataLoaded(null);
  };

  const downloadSample = () => {
    let sampleData = [];
    
    if (sampleType === 'barcode') {
      // Barcode sample - only ID column
      sampleData = [
        { ID: '1' },
        { ID: '2' },
        { ID: '3' },
        { ID: '4' },
        { ID: '5' }
      ];
    } else {
      // QR sample - ID, TEXT, URL columns
      sampleData = [
        { URL: 'https://sc.openbizit.in/api/zone', ID: '1', TEXT: 'Zone:1' },
        { URL: 'https://sc.openbizit.in/api/zone', ID: '2', TEXT: 'Zone:2' },
        { URL: 'https://sc.openbizit.in/api/zone', ID: '3', TEXT: 'Zone:3' },
        { URL: 'https://sc.openbizit.in/api/zone', ID: '4', TEXT: 'Zone:4' },
        { URL: 'https://sc.openbizit.in/api/zone', ID: '5', TEXT: 'Zone:5' }
      ];
    }

    const ws = XLSX.utils.json_to_sheet(sampleData);
    const wb = XLSX.utils.book_new();
    const sheetName = sampleType === 'barcode' ? 'Barcodes' : 'QR Codes';
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    
    // Set column widths
    if (sampleType === 'barcode') {
      ws['!cols'] = [
        { wch: 15 } // ID column
      ];
    } else {
      ws['!cols'] = [
        { wch: 40 }, // URL
        { wch: 15 }, // ID
        { wch: 20 }  // TEXT
      ];
    }

    const fileName = sampleType === 'barcode' ? 'barcode-sample.xlsx' : 'qr-sample.xlsx';
    XLSX.writeFile(wb, fileName);
  };

  return (
    <div className="excel-upload">
      <div className="upload-header">
        <h4>{title}</h4>
        <button 
          className="btn btn-secondary btn-sm"
          onClick={downloadSample}
          type="button"
        >
          📥 Download Sample
        </button>
      </div>

      <p className="upload-description">{description}</p>

      <div 
        className={`drop-zone ${file ? 'file-loaded' : ''}`}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          onChange={handleFileUpload}
          className="file-input"
          id="excel-upload"
        />
        <label htmlFor="excel-upload" className="file-label">
          {file ? (
            <div className="file-info">
              <span className="file-icon">📄</span>
              <span className="file-name">{file.name}</span>
              <span className="file-size">({(file.size / 1024).toFixed(1)} KB)</span>
              {rowCount > 0 && (
                <span className="file-rows">{rowCount} rows loaded</span>
              )}
            </div>
          ) : (
            <div className="upload-prompt">
              <span className="upload-icon">📁</span>
              <p>Drag & drop your Excel file here</p>
              <p className="upload-subtext">or click to browse</p>
              <small className="upload-limits">
                Supports .xlsx, .xls · Max {maxRows} rows
              </small>
            </div>
          )}
        </label>
      </div>

      {file && (
        <div className="file-actions">
          <button 
            className="btn btn-danger btn-sm"
            onClick={handleRemoveFile}
            type="button"
          >
            🗑️ Remove File
          </button>
        </div>
      )}

      {error && (
        <div className="alert alert-error">
          <span className="alert-icon">❌</span>
          <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{error}</pre>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <span className="alert-icon">✅</span>
          {success}
        </div>
      )}

      {uploading && (
        <div className="upload-progress">
          <span className="spinner"></span>
          Processing Excel file...
        </div>
      )}

      <div className="upload-requirements">
        {sampleType === 'barcode' ? (
          <>
            <small>
              <strong>Required column:</strong> ID (case insensitive)
            </small>
            <br />
            <small>
              <strong>Optional column:</strong> TEXT (will use ID if not provided)
            </small>
          </>
        ) : (
          <>
            <small>
              <strong>Required columns:</strong> ID, TEXT (case insensitive)
            </small>
            <br />
            <small>
              <strong>Optional column:</strong> URL (base URL for QR codes)
            </small>
          </>
        )}
        <br />
        <small>
          <strong>Max rows:</strong> {maxRows} per upload
        </small>
      </div>
    </div>
  );
};

export default ExcelUpload;